import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent
} from "react";
import {
  Backpack,
  Home,
  Lightbulb,
  Undo2
} from "lucide-react";

import type { ActivityExecutionContext } from "@/app/integration/contracts";
import { useCurriculumFeedbackAudio } from "@/features/audio";
import {
  CurriculumVoiceButton,
  PrimaryCommandButton
} from "@/ui";

import {
  HabitsFeedbackNotice,
  HabitsInstructionHeader,
  HabitsResult,
  HabitsSceneImage
} from "./PlayerChrome";
import type { SchoolbagPlayerProps } from "./player-contracts";
import {
  useCompletionReporting,
  usePlayerFocus
} from "./player-lifecycle";
import type { SchoolbagAction } from "./types";

export function SchoolbagPlayer({
  audioPlayer,
  binding,
  onAudioReplay,
  onComplete,
  onReplay,
  reducedMotion
}: SchoolbagPlayerProps) {
  const titleId = useId();
  const instructionId = useId();
  const itemsTitleId = useId();
  const zonesTitleId = useId();
  const context = useMemo<ActivityExecutionContext>(
    () => ({
      nowMs: Date.now,
      reducedMotion
    }),
    [reducedMotion]
  );
  const payload = binding.level.payload;
  const [state, setState] = useState(() =>
    binding.adapter.createInitialState(payload, context)
  );
  const firstControlRef = useRef<HTMLButtonElement>(null);
  const resultHeadingRef = useRef<HTMLHeadingElement>(null);

  const dispatch = useCallback(
    (action: SchoolbagAction) => {
      setState((currentState) =>
        binding.adapter.reduce(
          currentState,
          payload,
          action,
          context
        )
      );
    },
    [binding, context, payload]
  );

  const model = useMemo(
    () => binding.adapter.toRenderModel(state, payload),
    [binding, payload, state]
  );
  const metrics = useMemo(
    () => binding.adapter.getMetrics(state, payload),
    [binding, payload, state]
  );

  const feedbackAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    model.feedback,
    model.phase === "play"
  );

  useCompletionReporting(metrics, onComplete);
  usePlayerFocus(
    metrics.completed,
    firstControlRef,
    resultHeadingRef
  );

  const selectItem = useCallback(
    (
      itemId: string,
      selected: boolean,
      inputMethod: "keyboard" | "pointer"
    ) => {
      dispatch(
        selected
          ? { type: "cancel-selection" }
          : { type: "select-item", itemId, inputMethod }
      );
    },
    [dispatch]
  );

  const selectDropZone = useCallback(
    (
      dropZoneId: string,
      inputMethod: "keyboard" | "pointer"
    ) => {
      dispatch({
        type: "select-drop-zone",
        dropZoneId,
        inputMethod
      });
    },
    [dispatch]
  );

  const handleItemKeyDown = useCallback(
    (
      event: KeyboardEvent<HTMLButtonElement>,
      itemId: string,
      selected: boolean
    ) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      selectItem(itemId, selected, "keyboard");
    },
    [selectItem]
  );

  const handleDropZoneKeyDown = useCallback(
    (
      event: KeyboardEvent<HTMLButtonElement>,
      dropZoneId: string
    ) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      selectDropZone(dropZoneId, "keyboard");
    },
    [selectDropZone]
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLButtonElement>, dropZoneId: string) => {
      event.preventDefault();
      const itemId = event.dataTransfer.getData("text/plain");

      dispatch({
        type: "pointer-drop",
        itemId,
        dropZoneId
      });
    },
    [dispatch]
  );

  const handleReplay = useCallback(() => {
    dispatch({ type: "replay" });
    onReplay?.(binding.level.id);
  }, [binding.level.id, dispatch, onReplay]);

  const selectedItem = model.items.find(({ selected }) => selected);

  return (
    <section
      aria-labelledby={titleId}
      className="habits-player habits-player--schoolbag"
      data-activity-type={binding.activityType}
      data-reduced-motion={reducedMotion}
    >
      <HabitsInstructionHeader
        difficulty={model.difficulty}
        instruction={model.instruction}
        instructionId={instructionId}
        instructionPlaying={audioPlayer.isPlayingPath(
          model.instruction.audioPath
        )}
        levelId={model.levelId}
        onAudioReplay={onAudioReplay}
        title={model.title}
        titleId={titleId}
      />
      <HabitsSceneImage src={model.sceneImagePath} />

      {model.phase === "result" && model.result !== null ? (
        <HabitsResult
          description="书包和收纳篮都收拾妥当啦。"
          metrics={model.result}
          onReplay={handleReplay}
          resultHeadingRef={resultHeadingRef}
        />
      ) : (
        <>
          <div
            aria-describedby={instructionId}
            className="habits-schoolbag__workspace"
          >
            <section aria-labelledby={itemsTitleId}>
              <h3 id={itemsTitleId}>桌上的物品</h3>
              <div className="habits-schoolbag__items">
                {model.items.map((item, index) => (
                  <div
                    className="habits-schoolbag__item-wrap"
                    key={item.id}
                  >
                  <button
                    aria-label={
                      item.placedInDropZoneLabel === null
                        ? "选择" + item.label
                        : item.label +
                          "，已放到" +
                          item.placedInDropZoneLabel
                    }
                    aria-pressed={item.selected}
                    className="habits-schoolbag__item"
                    data-placed={item.placedInDropZoneId !== null}
                    disabled={item.disabled}
                    draggable={!item.disabled}
                    onClick={() =>
                      selectItem(item.id, item.selected, "pointer")
                    }
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", item.id);
                    }}
                    onKeyDown={(event) =>
                      handleItemKeyDown(event, item.id, item.selected)
                    }
                    ref={index === 0 ? firstControlRef : undefined}
                    type="button"
                  >
                    <span className="habits-schoolbag__item-label">
                      {item.label}
                    </span>
                    <span className="habits-schoolbag__item-status">
                      {item.placedInDropZoneLabel ??
                        (item.selected ? "已选中" : "待整理")}
                    </span>
                  </button>
                    <CurriculumVoiceButton
                      isPlaying={audioPlayer.isPlayingPath(item.audioPath)}
                      label={item.label}
                      onPlay={() =>
                        onAudioReplay({
                          audioPath: item.audioPath,
                          caption: item.label,
                          kind: "item",
                          levelId: model.levelId
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            </section>

            <section aria-labelledby={zonesTitleId}>
              <h3 id={zonesTitleId}>整理位置</h3>
              <div className="habits-schoolbag__zones">
                {model.dropZones.map((dropZone) => {
                  const ZoneIcon =
                    dropZone.id === "schoolbag" ? Backpack : Home;

                  return (
                    <div
                      className="habits-schoolbag__zone-wrap"
                      key={dropZone.id}
                    >
                    <button
                      aria-disabled={!dropZone.enabled}
                      aria-label={"放到" + dropZone.label}
                      className="habits-schoolbag__zone"
                      data-enabled={dropZone.enabled}
                      onClick={() =>
                        selectDropZone(dropZone.id, "pointer")
                      }
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(event) =>
                        handleDrop(event, dropZone.id)
                      }
                      onKeyDown={(event) =>
                        handleDropZoneKeyDown(event, dropZone.id)
                      }
                      type="button"
                    >
                      <ZoneIcon
                        aria-hidden="true"
                        className="habits-schoolbag__zone-icon"
                      />
                      <strong>{dropZone.label}</strong>
                      <span>
                        {dropZone.placedItems.length === 0
                          ? "还没有物品"
                          : dropZone.placedItems
                              .map(({ label }) => label)
                              .join("、")}
                      </span>
                    </button>
                      <CurriculumVoiceButton
                        isPlaying={audioPlayer.isPlayingPath(
                          dropZone.audioPath
                        )}
                        label={dropZone.label}
                        onPlay={() =>
                          onAudioReplay({
                            audioPath: dropZone.audioPath,
                            caption: dropZone.label,
                            kind: "drop-zone",
                            levelId: model.levelId
                          })
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          <p
            aria-live="polite"
            className="habits-schoolbag__selection"
          >
            {selectedItem === undefined
              ? "还没有选中物品"
              : "已选：" + selectedItem.label}
          </p>

          <HabitsFeedbackNotice
            feedback={model.feedback}
            isPlaying={feedbackAudio.isPlaying}
            onReplay={() => {
              if (model.feedback !== null) {
                onAudioReplay({
                  audioPath: model.feedback.audioPath,
                  caption: model.feedback.text,
                  kind: "feedback",
                  levelId: model.levelId
                });
              }
            }}
          />

          <div className="habits-player__actions">
            <PrimaryCommandButton
              disabled={!model.canUndo}
              icon={Undo2}
              onClick={() => dispatch({ type: "undo" })}
            >
              撤回一步
            </PrimaryCommandButton>
            <PrimaryCommandButton
              disabled={!model.canRequestHint}
              icon={Lightbulb}
              onClick={() => dispatch({ type: "request-hint" })}
            >
              给我提示
            </PrimaryCommandButton>
          </div>
        </>
      )}
    </section>
  );
}
